import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'shaonian');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const cat = useMemo(() => categories.find((c) => c.slug === activeCategory), [activeCategory]);

  const filtered = useMemo(() => {
    let result = comics.filter((c) => c.category === activeCategory);
    if (sortBy === 'popular') result = [...result].sort((a, b) => b.views - a.views);
    if (sortBy === 'rating') result = [...result].sort((a, b) => b.rating - a.rating);
    if (sortBy === 'latest')
      result = [...result].sort(
        (a, b) =>
          new Date(b.chapters[0]?.updatedAt || '').getTime() -
          new Date(a.chapters[0]?.updatedAt || '').getTime()
      );
    return result;
  }, [activeCategory, sortBy]);

  return (
    <>
      <main className="pt-16 md:pt-20">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: cat?.name || '漫画分类' },
          ]}
        />

        {/* Category Header */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-3">
              {cat?.name || '漫画分类'}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              {cat?.name
                ? `浏览蜜桃少年漫画精选${cat.name}作品，漫画全集免费阅读下拉式体验，在线下拉浏览完整故事。`
                : '蜜桃少年漫画提供丰富的漫画分类，免费在线阅读，下拉式流畅体验。'}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="border-b border-background-200/70 sticky top-14 md:top-16 z-40 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-2 px-1">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  onClick={() => setActiveCategory(c.slug)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === c.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Sort & Grid */}
        <section className="py-8 md:py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-900">{filtered.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {([
                  { value: 'latest', label: '最新' },
                  { value: 'popular', label: '最热' },
                  { value: 'rating', label: '评分' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-4 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {filtered.map((comic) => {
                  const comicCat = categories.find((c) => c.slug === comic.category);
                  return (
                    <ComicCard
                      key={comic.id}
                      slug={comic.slug}
                      title={comic.title}
                      author={comic.author}
                      cover={comic.cover}
                      category={comic.category}
                      categoryName={comicCat?.name || ''}
                      status={comic.status}
                      rating={comic.rating}
                      views={comic.views}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无漫画，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${cat?.name || '漫画分类'} - 蜜桃少年漫画`,
          description: `蜜桃少年漫画${cat?.name || ''}漫画全集免费阅读下拉式，在线下拉浏览完整故事。`,
          url: `https://mitaoshaonian.com/category/${activeCategory}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '蜜桃少年漫画',
            url: 'https://mitaoshaonian.com/',
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mitaoshaonian.com/' },
              { '@type': 'ListItem', position: 2, name: cat?.name || '漫画分类' },
            ],
          },
        })}
      </script>
    </>
  );
}